import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

import AnimatedLynkIcon from 'src/features/lynk/components/animated-lynk-icon'
import { ArrowRightIcon } from 'src/features/register-business/icons/arrow-right-icon'
import AISearchIdleView from 'src/features/search/views/ai-search-idle-view'
import AISearchResultsView from 'src/features/search/views/ai-search-results-view'
import GenericSearchResults from 'src/features/search/views/generic-search-results-view'
import SearchHistoryView from 'src/features/search/views/search-history-view'
import { queries } from 'src/lib/query-keys'
import { cn } from 'src/shared/utils/cn'

import { SearchIcon } from '../../navigation/icons/search-icon'
import useSearchMenuActiveStore from '../store/search-menu-active-store'

type SearchView = 'history' | 'generic-results' | 'ai-idle' | 'ai-results'

const AI_PLACEHOLDER_EXAMPLES = [
  'Show me my most expensive business',
  'Show me businesses missing emails',
  'Who should I follow up with this week?',
]

const ProfessionalAppFrameSearchBar = () => {
  const [searchText, setSearchText] = useState<string>('')
  const { isFocused, setIsFocused } = useSearchMenuActiveStore()
  const [mode, setMode] = useState<'ai-search' | 'search'>('search')
  const [activeView, setActiveView] = useState<SearchView>('history')
  const [aiCommittedQuery, setAiCommittedQuery] = useState<string | null>(null)
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const placeholderFadeTimeoutRef = useRef<number | null>(null)
  const queryClient = useQueryClient()
  const aiSearchFetchCount = useIsFetching({
    queryKey: aiCommittedQuery ? queries.search.lynk(aiCommittedQuery).queryKey : undefined,
  })
  const isAiSearchLoading = Boolean(aiCommittedQuery) && aiSearchFetchCount > 0
  const shouldAnimatePlaceholder = isFocused && searchText.length === 0
  const animatedPlaceholderText = AI_PLACEHOLDER_EXAMPLES[placeholderIndex] ?? ''

  useEffect(() => {
    const handleOutsidePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) {
        setIsFocused(false)
        inputRef.current?.blur()
      }
    }

    document.addEventListener('mousedown', handleOutsidePointerDown)
    document.addEventListener('touchstart', handleOutsidePointerDown)

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown)
      document.removeEventListener('touchstart', handleOutsidePointerDown)
    }
  }, [])

  useEffect(() => {
    if (isFocused) return
    setMode('search')
    setSearchText('')
    setActiveView('history')
    setAiCommittedQuery(null)
  }, [isFocused])

  useEffect(() => {
    setPlaceholderIndex(0)
    setIsPlaceholderVisible(true)
  }, [mode])

  useEffect(() => {
    if (!shouldAnimatePlaceholder) {
      setIsPlaceholderVisible(true)
      return
    }

    const intervalId = window.setInterval(() => {
      setIsPlaceholderVisible(false)

      if (placeholderFadeTimeoutRef.current) {
        window.clearTimeout(placeholderFadeTimeoutRef.current)
      }

      placeholderFadeTimeoutRef.current = window.setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % AI_PLACEHOLDER_EXAMPLES.length)
        setIsPlaceholderVisible(true)
      }, 220)
    }, 2400)

    return () => {
      window.clearInterval(intervalId)

      if (placeholderFadeTimeoutRef.current) {
        window.clearTimeout(placeholderFadeTimeoutRef.current)
        placeholderFadeTimeoutRef.current = null
      }
    }
  }, [shouldAnimatePlaceholder])

  useEffect(() => {
    if (mode !== 'search') return
    setActiveView(searchText.trim() ? 'generic-results' : 'history')
  }, [mode, searchText])

  const handleCommitAiSearch = useCallback(() => {
    const trimmedQuery = searchText.trim()
    if (!trimmedQuery) return
    setAiCommittedQuery(trimmedQuery)
    setActiveView('ai-results')
  }, [searchText])

  const handleSwitchToSearchMode = useCallback(() => {
    setMode('search')
    setSearchText('')
    setAiCommittedQuery(null)
    setActiveView('history')
  }, [])

  const handleActionButtonClick = useCallback(() => {
    if (isAiSearchLoading) return
    inputRef.current?.focus()
    if (mode === 'search') {
      const trimmedQuery = searchText.trim()
      setMode('ai-search')

      if (!trimmedQuery) {
        setSearchText('')
        setAiCommittedQuery(null)
        setActiveView('ai-idle')
        return
      }

      setSearchText(trimmedQuery)
      setAiCommittedQuery(trimmedQuery)
      setActiveView('ai-results')
      queryClient.invalidateQueries({
        queryKey: queries.search.lynk(trimmedQuery).queryKey,
      })
      return
    }
    handleCommitAiSearch()
  }, [handleCommitAiSearch, isAiSearchLoading, mode, queryClient, searchText])

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isAiSearchLoading) return
      if (event.key !== 'Enter') return
      event.preventDefault()

      if (mode === 'search') {
        const trimmedQuery = searchText.trim()
        if (!trimmedQuery) return

        setMode('ai-search')
        setSearchText(trimmedQuery)
        setAiCommittedQuery(trimmedQuery)
        setActiveView('ai-results')
        queryClient.invalidateQueries({
          queryKey: queries.search.lynk(trimmedQuery).queryKey,
        })
        return
      }

      handleCommitAiSearch()
    },
    [handleCommitAiSearch, isAiSearchLoading, mode, queryClient, searchText],
  )

  const handleSelectHistoryAiQuery = useCallback(
    (rawQuery: string) => {
      const trimmedQuery = rawQuery.trim()
      if (!trimmedQuery) return

      setIsFocused(true)
      setMode('ai-search')
      setSearchText(trimmedQuery)
      setAiCommittedQuery(trimmedQuery)
      setActiveView('ai-results')

      queryClient.invalidateQueries({
        queryKey: queries.search.lynk(trimmedQuery).queryKey,
      })
    },
    [queryClient, setIsFocused],
  )

  const renderSearchResultView = useCallback(() => {
    switch (activeView) {
      case 'history':
        return <SearchHistoryView onSelectSearchQuery={handleSelectHistoryAiQuery} />
      case 'generic-results':
        return <GenericSearchResults searchText={searchText} />
      case 'ai-idle':
        return <AISearchIdleView />
      case 'ai-results':
        return <AISearchResultsView committedQuery={aiCommittedQuery ?? ''} />
      default:
        return null
    }
  }, [activeView, aiCommittedQuery, handleSelectHistoryAiQuery, searchText])

  return (
    <div ref={containerRef} className="relative z-10 shrink-0">
      {/* Warm up DotLottie on initial mount to avoid first-visible lag. */}
      <div className="pointer-events-none absolute -z-10 size-0 overflow-hidden opacity-0" aria-hidden>
        <AnimatedLynkIcon autoPlay={false} loop={false} variant="gradient" width={1} height={1} />
      </div>

      <div
        className={cn(
          `relative z-20 flex h-[45px] shrink-0 flex-row items-center justify-start gap-2.5 rounded-full py-3 pl-3 pr-2 transition-[width,background-color] duration-300 ease-out`,
          isFocused ? 'bg-white' : 'bg-white/10',
          mode === 'ai-search' && 'border border-secondaryInverse',
        )}
        style={{ width: isFocused ? 400 : 250 }}
      >
        {mode === 'search' ? (
          <SearchIcon
            className={`size-[20px] transition-colors duration-300 ${isFocused ? 'text-primary' : 'text-[#c6cbc9]'}`}
          />
        ) : (
          <button onClick={handleSwitchToSearchMode}>
            <AnimatedLynkIcon autoPlay={isFocused} variant="gradient" width={26} height={26} />
          </button>
        )}
        <div className="relative flex-1">
          <input
            type="text"
            placeholder={isFocused ? '' : mode === 'ai-search' ? 'Ask me anything...' : 'Ask Lynk'}
            className={cn(
              'w-full bg-transparent text-[13px] font-medium transition-colors duration-300 focus:outline-none',
              isFocused ? 'text-primary placeholder:text-muted' : 'text-white placeholder:text-[#c6cbc9]',
            )}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleInputKeyDown}
            disabled={isAiSearchLoading}
            ref={inputRef}
          />

          {shouldAnimatePlaceholder && (
            <span
              className={cn(
                'pointer-events-none absolute inset-y-0 left-0 flex items-center text-[13px] font-medium text-muted transition-opacity duration-200',
                isPlaceholderVisible ? 'opacity-100' : 'opacity-0',
              )}
            >
              {animatedPlaceholderText}
            </span>
          )}
        </div>
        {isFocused && !(mode === 'ai-search' && searchText.length === 0) && (
          <button
            type="button"
            className={cn(
              'flex size-[35px] items-center justify-center rounded-full bg-primary transition-opacity',
              isAiSearchLoading && 'cursor-not-allowed opacity-50',
            )}
            onClick={handleActionButtonClick}
            disabled={isAiSearchLoading}
          >
            {mode === 'search' ? (
              <AnimatedLynkIcon autoPlay={false} variant="gradient" width={26} height={26} />
            ) : (
              <ArrowRightIcon className="size-4 text-white" />
            )}
          </button>
        )}
      </div>

      <div
        className={`absolute left-[-6px] top-[-6px] z-10 max-h-[725px] min-h-[525px] overflow-y-auto transition-all duration-300 ease-out ${
          isFocused ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
        }`}
        style={{
          width: 412,
          borderRadius: '26px 26px 22.5px 22.5px',
          background: 'rgba(255, 255, 255, 0.80)',
          border: '1px solid rgba(255, 255, 255, 0.45)',
          backdropFilter: 'blur(24px) saturate(160%)',
          WebkitBackdropFilter: 'blur(24px) saturate(160%)',
          boxShadow: '0 0 30px 0 #FFF inset, 0 2px 60px 0 rgba(27, 48, 41, 0.15)',
        }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            borderRadius: '26px 26px 22.5px 22.5px',
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.18) 45%, rgba(255,255,255,0.06) 100%)',
            mixBlendMode: 'screen',
          }}
        />
        <div className="z-10 flex flex-col gap-2 px-4 pb-4 pt-[60px]">{renderSearchResultView()}</div>
      </div>
    </div>
  )
}

export default ProfessionalAppFrameSearchBar
